import type { FC } from "react"
import React from "react"
import {
  Stack,
  Text,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Icon,
} from "@chakra-ui/react"

import { useAddress } from "web3/hooks/useAddress"
import type { Governor } from "query/graphql"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import WavesIcon from "ui/components/icons/WavesIcon"
import { useModals } from "common/hooks/useModals"
import { useChainReference } from "web3/hooks/useChainReference"
import { getAccountIdParams } from "web3/helpers/accountId"

type Props = {
  governance: Governor
}

const VoteOnboarding: FC<Props> = ({ governance }) => {
  const { voteOnboarding } = useModals()
  const address = useAddress()
  const chainReference = useChainReference()

  const renderOnboarding = ({
    chainReference,
    governance,
  }: {
    chainReference?: number
    governance: Governor
  }) => {
    if (!address || !chainReference) return <WalletConnectedOnboarding />

    const { chainId } = getAccountIdParams(governance?.id)
    const { reference: governanceReference } = getChainIdParams(chainId)

    if (chainReference !== governanceReference)
      return (
        <MissmatchingChainReferenceOnboarding
          chainReference={chainReference}
          governance={governance}
        />
      )

    return <PendingStateOnboarding />
  }

  return (
    <Modal
      isCentered
      isOpen={voteOnboarding.isOpen}
      onClose={() => voteOnboarding.onClose()}
    >
      <ModalOverlay />
      <ModalContent minW="30rem">
        <ModalHeader borderBottom="gray.light" minH="3rem">
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody p={6}>
          <ModalCloseButton />
          {renderOnboarding({ governance, chainReference })}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default VoteOnboarding

const WalletConnectedOnboarding = () => {
  const { walletSelection } = useModals()

  const handleConnectWalletClick = (): void => {
    walletSelection.onOpen()
  }

  return (
    <Stack align="center" pb={8} px={12} spacing={8}>
      <Text as="h5" textStyle="h5">
        Connect your wallet
      </Text>
      <Text color="gray.500" textAlign="center" textStyle="body.bold.md">
        Please, sign in with your Ethereum wallet
      </Text>
      <Stack>
        <Button variant="primary" onClick={handleConnectWalletClick}>
          Connect wallet
        </Button>
      </Stack>
    </Stack>
  )
}

const MissmatchingChainReferenceOnboarding: FC<{
  governance: Governor
  chainReference: number
}> = ({ chainReference, governance }) => {
  const { reference: governanceReference } = getChainIdParams(governance.id)

  return (
    <Stack align="center" pb={16} px={12} spacing={8}>
      <Text as="h5" textStyle="h5">
        Incorrect chain
      </Text>
      <Text color="gray.500" textAlign="center" textStyle="body.bold.md">
        You currently have selected {getChainReferenceName(chainReference)} but
        the governance works only on{" "}
        {getChainReferenceName(governanceReference)}
      </Text>
    </Stack>
  )
}

const PendingStateOnboarding = () => {
  return (
    <>
      <Stack align="flex-start" spacing={4}>
        <Text textStyle="lg">Proposal delay period</Text>
        <Text textStyle="md">
          This protocol has a delay period from when a proposal is submitted to
          when voting starts. During this time, you can set up your wallet for
          voting or change your delegation.
        </Text>
        <Text textStyle="md">Why is this useful?</Text>
        <Text textStyle="md">
          Once the voting period starts, it will not be possible to make any
          changes to your voting power. So be sure to change your delegation now
          if you need to make any adjustments.
        </Text>
      </Stack>
      <Icon
        as={WavesIcon}
        fill="white"
        h={56}
        mb={-4}
        ml={-4}
        rounded="1rem"
        w="28rem"
      />
    </>
  )
}
